import { useState, useCallback } from 'react'
import { uploadFileXHR, type UploadOptions } from '@/services/api'
import type { UploadResponse } from '@/types'

interface UploadState {
  isUploading: boolean
  progress: number
  error: string | null
}

export function useUpload() {
  const [state, setState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    error: null,
  })

  const upload = useCallback(async (file: File, options: UploadOptions = {}): Promise<UploadResponse> => {
    setState({ isUploading: true, progress: 0, error: null })

    try {
      const response = await uploadFileXHR(file, {
        ...options,
        onProgress: (percent) => setState((prev) => ({ ...prev, progress: percent })),
      })

      if (response.success) {
        setState({ isUploading: false, progress: 100, error: null })
      } else {
        setState({ isUploading: false, progress: 0, error: response.error || 'Upload failed' })
      }

      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed'
      setState({ isUploading: false, progress: 0, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }, [])

  const reset = useCallback(() => {
    setState({ isUploading: false, progress: 0, error: null })
  }, [])

  return { ...state, upload, reset }
}
